import { toast } from 'sonner'
import { paths } from '@/routes/paths'
import { useState, useEffect } from 'react'
import axios, { endpoints } from '@/lib/axios'
import { useBoolean } from '@/hooks/use-boolean'
import { useRouter } from '@/routes/hooks/use-router'
import { PaginateResponse, PaginationMeta } from '@/utils/paginate'
import CustomPagination from '@/components/pagination/custom-pagination'
import ConfirmDialog from '@/components/share-components/confirm-dialog'
import LoadingSection from '@/components/loading-indicator/loading-section'

import PrivilegeTable from '../privilege-table'
import PrivilegeFilter from '../privilege-filter'
import {
  PrivilegeType,
  type Privilege,
  type PrivilegeFilters,
} from '../privilege.types'

const PAGE_SIZE = 10

export default function PrivilegeView() {
  const router = useRouter()
  const tableLoading = useBoolean(true)
  const [privileges, setPrivileges] = useState<Privilege[]>([])
  const [pageMeta, setPageMeta] = useState<PaginationMeta>({
    limit: PAGE_SIZE,
    page: 1,
    total: 0,
  })
  const [filters, setFilters] = useState<PrivilegeFilters>({
    page: 1,
    limit: PAGE_SIZE,
    search: '',
    type: PrivilegeType.ALL,
    isEnable: undefined,
    orderBy: 'createdAt:desc',
  })

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    privilege: Privilege | null
  }>({
    open: false,
    privilege: null,
  })

  // Loading state for delete operation
  const [deleteLoading, setDeleteLoading] = useState(false)

  const fetchPrivileges = async () => {
    try {
      tableLoading.onTrue()

      console.log('filters', filters)

      // remove type all and undefined value from filters
      const { type, ...rest } = filters
      const safeFilters = {
        ...rest,
        ...(type !== PrivilegeType.ALL && { type }),
      }

      const { data } = await axios.get<PaginateResponse<Privilege>>(
        endpoints.privileges.list,
        {
          params: {
            ...safeFilters,
          },
        },
      )

      console.log('privileges', data)

      setPrivileges(data.items)
      setPageMeta({
        limit: data.limit,
        page: data.page,
        total: data.total,
      })
    } catch {
      toast.error('เกิดข้อผิดพลาดในการโหลดข้อมูล')
    } finally {
      tableLoading.onFalse()
    }
  }

  useEffect(() => {
    fetchPrivileges()
  }, [filters])

  const handleFiltersChange = (newFilters: PrivilegeFilters) => {
    setFilters(newFilters)
  }

  const handleAddClick = () => {
    router.push(paths.privilegeCreate)
  }

  const handleEdit = (privilege: Privilege) => {
    router.push(paths.privilegeEdit(privilege.id))
  }

  const handleRedeemCondition = (privilege: Privilege) => {
    router.push(paths.redeemCondition(privilege.id))
  }

  const handleDelete = (privilege: Privilege) => {
    setConfirmDialog({
      open: true,
      privilege,
    })
  }

  const handleConfirmDelete = async () => {
    if (!confirmDialog.privilege || deleteLoading) return

    try {
      setDeleteLoading(true)

      await axios.delete(
        endpoints.privileges.delete(confirmDialog.privilege.id),
      )

      toast.success('ลบของรางวัลสำเร็จ')

      // Refresh the list
      fetchPrivileges()
    } catch {
      toast.error('เกิดข้อผิดพลาดในการลบข้อมูล')
    } finally {
      setDeleteLoading(false)
    }
  }

  const handlePageChange = (page: number) => {
    setFilters({
      ...filters,
      page,
    })
  }

  return (
    <div className="w-full">
      <div className="space-y-6">
        {/* Filter Section - Always visible */}
        <PrivilegeFilter
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onAddClick={handleAddClick}
        />

        {/* Table Section - With loading state */}
        <LoadingSection loading={tableLoading.value}>
          <div className="space-y-6">
            <PrivilegeTable
              privileges={privileges}
              loading={tableLoading.value}
              onEdit={handleEdit}
              onRedeemCondition={handleRedeemCondition}
              onDelete={handleDelete}
            />

            {/* Pagination */}
            {Math.ceil(pageMeta.total / pageMeta.limit) > 1 && (
              <CustomPagination
                currentPage={filters.page}
                totalPages={Math.ceil(pageMeta.total / pageMeta.limit)}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </LoadingSection>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ open, privilege: null })}
        title="ยืนยันการลบ"
        description={`คุณต้องการลบของรางวัล "${confirmDialog.privilege?.name}" หรือไม่? การดำเนินการนี้ไม่สามารถยกเลิกได้`}
        confirmText="ลบ"
        cancelText="ยกเลิก"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
      />
    </div>
  )
}
